import DropdownOption from './DropdownOption';
import { DropdownOptionItem } from './DropdownOption/DropdownOptionProps';
import React, { useRef, useState } from 'react';
import { StyledCloseButton, StyledDropdownMenu } from './StyledDropdown';
import { Box, Divider, Input } from '@chakra-ui/react';
import { DropdownMenuProps } from './DropdownProps';
import { BEPopover, PopoverProps } from '@molecules/BEOffScreen/Popover';
import styled from '@emotion/styled';
import BEIcon from '@molecules/BEIcon';

const StyledMenuHeader = styled(Box)`
  padding: 8px;
  font-size: 18px;
  font-weight: 600;
`;

const DropdownMenu = ({
  inputRef,
  multiple,
  name,
  options,
  placeholder,
  onSelect,
  showCheck,
  showHeader = false,
  value,
  ...props
}: DropdownMenuProps & PopoverProps) => {
  const optionsRef = useRef<React.RefObject<HTMLLIElement>[]>([]);
  const [selectedOptions, setSelectedOptions] = useState(value);

  const handleOptionClick = (e: React.MouseEvent<HTMLLIElement>) => {
    const input = (e.target as HTMLLIElement).querySelector('input');
    input?.click();
  };

  const getOptionIndex = (option: DropdownOptionItem) => {
    if (Array.isArray(selectedOptions)) {
      return selectedOptions.findIndex((v) => v === option.value);
    }
    return -1;
  };

  const isSelected = (option: DropdownOptionItem) => {
    if (option && Array.isArray(selectedOptions)) {
      return getOptionIndex(option) > -1;
    }

    return selectedOptions === option.value;
  };

  const _handleChange = (e) => {
    if (multiple) {
      const clickedOption = options?.find((o) => o.value === e.target.value);

      if (clickedOption && isSelected(clickedOption)) {
        const nextValue = [...selectedOptions].filter(
          (v) => v !== clickedOption.value
        );
        setSelectedOptions?.(nextValue);
        onSelect?.(nextValue);
      } else {
        const nextValue = [...selectedOptions, clickedOption?.value].sort(
          (a, b) =>
            (options?.findIndex((o) => o?.value === a) || 0) -
            (options?.findIndex((o) => o?.value === b) || 0)
        );
        setSelectedOptions?.(nextValue);
        onSelect?.(nextValue);
      }
    } else {
      setSelectedOptions?.(e.target.value);
      onSelect?.(e.target.value);
    }
  };

  return (
    <BEPopover {...props}>
      <StyledDropdownMenu>
        {showHeader && placeholder && (
          <>
            <StyledMenuHeader>{placeholder}</StyledMenuHeader>
            <Divider mb="8px" />
          </>
        )}
        <StyledCloseButton onClick={props.onCancel}>
          <BEIcon size="lg" name="close" weight="solid" />
        </StyledCloseButton>
        {(options || []).map((o, idx) => {
          const _isSelected = isSelected(o);

          return (
            <DropdownOption
              {...o}
              isSelected={_isSelected}
              showCheck={showCheck}
              key={o.value as string}
              onClick={handleOptionClick}
              ref={optionsRef.current[idx]}
              tabIndex={0}
            >
              <label htmlFor={o.id}>
                <Input
                  id={o.id}
                  name={name}
                  onChange={_handleChange}
                  tabIndex={-1}
                  ref={inputRef}
                  type={multiple ? 'checkbox' : 'radio'}
                  value={o.value}
                />
                {o.text}
              </label>
            </DropdownOption>
          );
        })}
      </StyledDropdownMenu>
    </BEPopover>
  );
};

export default DropdownMenu;
